import { Helpers } from '../../core';

/**
 * Shared utility functions for creating JSON-LD structured data
 */

/**
 * Extracts image URL from various image object formats
 * @param {string|Object|Array} image - Image in various formats
 * @returns {string|null} - Image URL or null
 */
export const getJsonLdImageUrl = image => {
    if (!image) return null;
    if (typeof image === 'string') return image;
    if (Array.isArray(image)) {
        const firstImage = image[0];
        return typeof firstImage === 'string'
            ? firstImage
            : firstImage?.image || firstImage?.src || firstImage?.cdn || firstImage?.url;
    }
    return image.cdn || image.lrg || image.sm || image.src || image.url || image.image;
};

/**
 * Creates an ImageObject for JSON-LD
 * @param {string} imageUrl - Image URL
 * @param {string} altText - Alt text / caption
 * @param {Object} options - Additional options (orientation, width, height)
 * @returns {Object|null} - ImageObject or null
 */
export const createJsonLdImageObject = (imageUrl, altText, options = {}) => {
    if (!imageUrl) return null;

    const { orientation, width, height } = options;
    const copyRight = Helpers.getCopyRightNotice() || '© Copyright 2026 HP Development Company, L.P.';

    return {
        '@type': 'ImageObject',
        contentUrl: imageUrl,
        ...(altText
            ? {
                  name: altText,
                  ...(orientation ? { caption: `${altText} - ${orientation}` } : { caption: altText }),
              }
            : {}),
        copyrightNotice: copyRight,
        ...(width && height ? { width, height } : {}),
    };
};

/**
 * Processes array of images into JSON-LD ImageObject array
 * @param {Array} images - Array of image objects
 * @param {Object} product - Product object (optional, for additional details)
 * @returns {Object|null} - Object with image array or null
 */
export const getJsonLdImageArrayData = (images, product) => {
    if (!Array.isArray(images) || images.length === 0) return null;

    return {
        image: images.reduce((allImages, img) => {
            const imageUrl = getJsonLdImageUrl(img);

            if (!imageUrl) return allImages;

            // Get additional details if product is provided
            if (product) {
                const { name } = product || {};
                const { orientation, alt, altText } = img || {};
                const [, queryString] = imageUrl.split('?');
                const { w, h } = Helpers.getSearch(queryString) || {};
                const altTextValue = alt || altText || name || '';

                allImages.push(
                    createJsonLdImageObject(imageUrl, altTextValue, {
                        orientation,
                        width: w,
                        height: h,
                    }),
                );
            } else {
                allImages.push(imageUrl);
            }

            return allImages;
        }, []),
    };
};

/**
 * Creates a Brand object for JSON-LD
 * @param {Object} options - Brand options
 * @param {string} options.brand - Brand name
 * @param {string} options.facet_subbrand - Subbrand name
 * @returns {Object|null} - Brand object or null
 */
export const createJsonLdBrand = ({ brand, facet_subbrand } = {}) => {
    const brandName = brand || facet_subbrand;

    if (!brandName) return null;

    return {
        brand: {
            '@type': 'Brand',
            name: brandName,
        },
    };
};

/**
 * Determines schema.org availability status
 * @param {boolean} instock - In stock status
 * @param {boolean} isOOS - Out of stock status
 * @returns {string} - Schema.org availability URL
 */
export const getJsonLdAvailability = (instock, isOOS) => {
    if (typeof instock === 'boolean') {
        return instock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock';
    }
    if (typeof isOOS === 'boolean') {
        return isOOS ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock';
    }
    return 'https://schema.org/InStock'; // Default
};

/**
 * Creates an AggregateRating object for JSON-LD
 * @param {Object} options - Rating options
 * @param {string} options.name - Product/item name
 * @param {number|Object} options.rating - Rating value or object with score/reviewsCount
 * @param {number} options.numReviews - Number of reviews
 * @param {string} options.itemType - Type of item being reviewed (default: 'IndividualProduct')
 * @returns {Object|null} - AggregateRating object or null
 */
export const createJsonLdAggregateRating = ({ name, rating, numReviews, itemType = 'IndividualProduct' } = {}) => {
    let ratingValue, reviewCount;

    // If rating is an object, get the score and number of reviews from it
    if (rating && typeof rating === 'object') {
        const { score, reviewsCount } = rating;
        ratingValue = score;
        reviewCount = typeof reviewsCount === 'number' ? reviewsCount : numReviews;
    } else {
        ratingValue = rating;
        reviewCount = numReviews;
    }

    // Only include rating if we have real review data
    const hasRatings = !isNaN(reviewCount) && reviewCount > 0;
    const hasValidRating = !isNaN(ratingValue);

    if (!hasValidRating || !hasRatings) return null;

    return {
        aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue,
            bestRating: '5',
            worstRating: '0',
            reviewCount,
            ...(name && itemType
                ? {
                      itemReviewed: {
                          '@type': itemType,
                          name,
                      },
                  }
                : {}),
        },
    };
};

/**
 * Creates an Offer object for JSON-LD
 * @param {Object} options - Offer options
 * @returns {Object|null} - Offer object or null
 */
export const createJsonLdOffer = ({
    sku,
    linkUrl,
    productUrl,
    url,
    instock,
    isOOS,
    salePrice,
    listPrice,
    regularPrice,
    price,
    currency = 'USD',
    shipCutOffTime,
} = {}) => {
    // Determine the price to use
    let offerPrice = price?.salePrice || price?.regularPrice || salePrice || regularPrice || listPrice;

    if (!offerPrice) return null;

    // Determine the URL
    let offerUrl = url || linkUrl || productUrl;
    if (typeof offerUrl === 'string' && /^\//.test(offerUrl)) {
        offerUrl = Helpers.getAbsoluteRedirectPath(offerUrl);
    }

    // Determine availability
    const oos = typeof price?.isOOS === 'boolean' ? price.isOOS : isOOS;
    const availability = getJsonLdAvailability(instock, oos);

    return {
        offers: {
            '@type': 'Offer',
            ...(sku ? { sku } : {}),
            price: offerPrice,
            priceCurrency: currency,
            ...(offerUrl ? { url: offerUrl } : {}),
            availability,
            ...(shipCutOffTime
                ? {
                      shippingDetails: {
                          '@type': 'OfferShippingDetails',
                          deliveryTime: {
                              cutoffTime: shipCutOffTime,
                          },
                      },
                  }
                : {}),
        },
    };
};

/**
 * Normalizes URL path by converting relative to absolute
 * @param {string} url - URL to normalize
 * @returns {string} - Normalized URL
 */
export const normalizeJsonLdUrl = url => {
    if (!url || typeof url !== 'string') return url;

    if (/^\//.test(url)) {
        return Helpers.getAbsoluteRedirectPath(url);
    }

    return url;
};
